import { Component } from '@theme/component';
import { morphSection } from '@theme/section-renderer';
import { fetchConfig } from '@theme/utilities';
import { cartPerformance } from '@theme/performance';
import { CartDiscountUpdateEvent, CartErrorEvent } from '@shopify/events';

/**
 * A custom element that applies a discount to the cart.
 *
 * @typedef {Object} CartDiscountComponentRefs
 * @property {HTMLElement} cartDiscountError - The error element.
 * @property {HTMLElement} cartDiscountErrorDiscountCode - The discount code error element.
 * @property {HTMLElement} cartDiscountErrorShipping - The shipping error element.
 * @property {HTMLElement} [couponPills] - The coupon pills carousel.
 */

/**
 * @typedef {Object} CartOffer
 * @property {string} code
 * @property {string} [title]
 * @property {string} [description]
 * @property {number} [minQuantity]
 * @property {number} [minPurchase]
 * @property {boolean} [requireAll]
 */

/**
 * @extends {Component<CartDiscountComponentRefs>}
 */
class CartDiscount extends Component {
  requiredRefs = ['cartDiscountError', 'cartDiscountErrorDiscountCode', 'cartDiscountErrorShipping'];

  /** @type {AbortController | null} */
  #activeFetch = null;

  static #OFFER_CODES_KEY = 'bc-pdp-offer-codes-v2';
  static #OFFER_CODES_KEY_LEGACY = 'bc-pdp-offer-codes';

  /** @type {string} */
  #defaultDiscountError = '';

  /** @type {CartOffer[]} */
  #offers = [];

  connectedCallback() {
    super.connectedCallback();
    this.#refreshCouponPills();
    this.#loadOfferCoupons();
  }

  updatedCallback() {
    super.updatedCallback();
    this.#refreshCouponPills();
  }

  /**
   * @returns {HTMLElement | null}
   */
  #pillsList() {
    const fromRef = this.refs.couponPills;
    if (fromRef instanceof HTMLElement) return fromRef;
    const fromDom = this.querySelector('[ref="couponPills"]');
    return fromDom instanceof HTMLElement ? fromDom : null;
  }

  #refreshCouponPills() {
    this.#renderCouponPills(this.#offers.length ? this.#offers : this.#readCachedOffers());
  }

  #createAbortController() {
    if (this.#activeFetch) {
      this.#activeFetch.abort();
    }

    const abortController = new AbortController();
    this.#activeFetch = abortController;
    return abortController;
  }

  /**
   * Shopify's Ajax cart endpoint is `/cart/update.js`.
   * @returns {string}
   */
  #cartUpdateUrl() {
    const url = Theme.routes.cart_update_url || '/cart/update';
    return url.endsWith('.js') ? url : `${url}.js`;
  }

  /**
   * @param {unknown} value
   * @returns {CartOffer | null}
   */
  #normalizeOffer(value) {
    if (typeof value === 'string') {
      const code = value.trim();
      return code ? this.#normalizeOffer({ code }) : null;
    }

    if (!value || typeof value !== 'object' || typeof /** @type {CartOffer} */ (value).code !== 'string') {
      return null;
    }

    const offer = /** @type {CartOffer} */ (value);
    const code = offer.code.trim();
    if (!code) return null;

    const description = typeof offer.description === 'string' ? offer.description.trim() : '';
    const title = typeof offer.title === 'string' ? offer.title.trim() : '';
    const parsed = this.#parseOfferRules(description, code);

    return {
      code,
      title,
      description,
      minQuantity: Number(offer.minQuantity) > 0 ? Number(offer.minQuantity) : parsed.minQuantity,
      minPurchase: Number(offer.minPurchase) > 0 ? Number(offer.minPurchase) : parsed.minPurchase,
      requireAll: offer.requireAll === false || parsed.requireAll === false ? false : true,
    };
  }

  /**
   * @param {unknown} value
   * @returns {string}
   */
  #escapeHtml(value) {
    return String(value ?? '')
      .replace(/&/g, '&amp;')
      .replace(/</g, '&lt;')
      .replace(/>/g, '&gt;')
      .replace(/"/g, '&quot;');
  }

  /**
   * @param {CartOffer} offer
   * @returns {string}
   */
  #offerHeadline(offer) {
    const title = typeof offer?.title === 'string' ? offer.title.trim() : '';
    const blob = `${title} ${offer?.description || ''}`.trim();
    const pct = blob.match(/(\d+)\s*%/);
    if (pct) return `${pct[1]}% OFF`;
    const rupee = blob.match(/₹\s*([\d,]+)/);
    if (rupee && /off/i.test(blob)) return `₹${rupee[1]} OFF`;
    if (title) return title;
    return (offer?.code || 'OFFER').toUpperCase();
  }

  /**
   * @param {string} description
   * @param {string} code
   * @returns {{ minQuantity: number, minPurchase: number, requireAll: boolean }}
   */
  #parseOfferRules(description, code) {
    const text = description || '';
    const qtyMatch =
      text.match(/any\s+(\d+)\s+(?:article|piece)s?/i) ||
      text.match(/(\d+)\+\s+(?:article|piece)s?/i);
    const amountMatch =
      text.match(/(?:above|over)\s*₹\s*([\d,]+)/i) ||
      text.match(/(?:above|over)\s*(?:rs\.?|inr)\s*([\d,]+)/i);

    const minQuantity = qtyMatch ? Number(qtyMatch[1]) : 0;
    const minPurchase = amountMatch ? Number(String(amountMatch[1]).replace(/,/g, '')) : 0;
    const requireAll = !(minQuantity > 0 && minPurchase > 0 && /\bor\b/i.test(text));

    return {
      minQuantity: Number.isFinite(minQuantity) ? minQuantity : 0,
      minPurchase: Number.isFinite(minPurchase) ? minPurchase : 0,
      requireAll,
    };
  }

  /**
   * @returns {CartOffer[]}
   */
  #readCachedOffers() {
    try {
      const cached =
        sessionStorage.getItem(CartDiscount.#OFFER_CODES_KEY) ||
        sessionStorage.getItem(CartDiscount.#OFFER_CODES_KEY_LEGACY);
      const parsed = cached ? JSON.parse(cached) : [];
      if (!Array.isArray(parsed)) return [];
      return parsed.map((entry) => this.#normalizeOffer(entry)).filter(Boolean);
    } catch {
      return [];
    }
  }

  /**
   * @param {CartOffer[]} offers
   */
  #writeCachedOffers(offers) {
    try {
      sessionStorage.setItem(CartDiscount.#OFFER_CODES_KEY, JSON.stringify(offers));
    } catch {
      // Ignore quota / private-mode failures.
    }
  }

  /**
   * @returns {string[]}
   */
  #extraSettingCodes() {
    const extra = this.dataset.extraCoupons || '';
    return extra
      .split(',')
      .map((entry) => entry.split('|')[0]?.trim() || '')
      .filter(Boolean);
  }

  /**
   * @param {Document} doc
   * @returns {CartOffer[]}
   */
  #parseOffersFromDoc(doc) {
    /** @type {CartOffer[]} */
    const offers = [];
    const seen = new Set();
    const nodes = [
      ...doc.querySelectorAll('[data-bc-offer-code], [data-bc-offer-copy]'),
    ];

    for (const element of nodes) {
      const card = element.closest('[data-bc-offer-code]') || element;
      const code = (
        card.getAttribute('data-bc-offer-code') ||
        element.getAttribute('data-bc-offer-copy') ||
        ''
      ).trim();
      if (!code) continue;

      const key = code.toUpperCase();
      if (seen.has(key)) continue;
      seen.add(key);

      const offer = this.#normalizeOffer({
        code,
        title: card.getAttribute('data-bc-offer-title') || '',
        description: card.getAttribute('data-bc-offer-description') || '',
      });
      if (offer) offers.push(offer);
    }

    return offers;
  }

  /**
   * @param {CartOffer[]} pdpOffers
   * @param {string[]} extraCodes
   * @returns {CartOffer[]}
   */
  #mergeOffers(pdpOffers, extraCodes) {
    const map = new Map();
    for (const offer of pdpOffers) {
      map.set(offer.code.toUpperCase(), offer);
    }
    for (const code of extraCodes) {
      if (!map.has(code.toUpperCase())) {
        const offer = this.#normalizeOffer({ code });
        if (offer) map.set(code.toUpperCase(), offer);
      }
    }
    return [...map.values()];
  }

  /**
   * @param {string} code
   * @returns {CartOffer | undefined}
   */
  #offerFor(code) {
    const needle = String(code || '').toUpperCase();
    const fromLive = this.#offers.find((offer) => offer.code.toUpperCase() === needle);
    if (fromLive) return fromLive;
    return this.#readCachedOffers().find((offer) => offer.code.toUpperCase() === needle);
  }

  /**
   * @param {Partial<CartOffer> | null | undefined} offer
   * @returns {{ eligible: boolean, message: string }}
   */
  #eligibilityFor(offer) {
    if (!offer?.code) return { eligible: true, message: '' };

    const itemCount = Number(this.dataset.cartItemCount || 0);
    const total = Number(this.dataset.cartTotal || 0);
    const minQuantity = Number(offer.minQuantity) || 0;
    const minPurchase = Number(offer.minPurchase) || 0;
    const qtyOk = minQuantity <= 0 || itemCount >= minQuantity;
    const amountOk = minPurchase <= 0 || total >= minPurchase * 100;
    const requireAll = offer.requireAll !== false;

    if (!requireAll && minQuantity > 0 && minPurchase > 0) {
      if (qtyOk || amountOk) return { eligible: true, message: '' };
      return {
        eligible: false,
        message: `${offer.code} applies on ${minQuantity}+ items or orders above ₹${minPurchase.toLocaleString('en-IN')}`,
      };
    }

    if (!qtyOk) {
      const more = minQuantity - itemCount;
      return {
        eligible: false,
        message: `Add ${more} more item${more === 1 ? '' : 's'} to apply ${offer.code}`,
      };
    }

    if (!amountOk) {
      return {
        eligible: false,
        message: `${offer.code} applies on orders above ₹${minPurchase.toLocaleString('en-IN')}`,
      };
    }

    return { eligible: true, message: '' };
  }

  /**
   * Loads customer-facing coupon codes from the PDP offer carousel.
   */
  #loadOfferCoupons = async () => {
    const sourceUrl = this.dataset.offerSourceUrl;
    const extraCodes = this.#extraSettingCodes();

    if (!sourceUrl) {
      const offers = this.#mergeOffers(this.#offers, extraCodes);
      this.#offers = offers;
      this.#writeCachedOffers(offers);
      this.#renderCouponPills(offers);
      return;
    }

    try {
      const sectionId = this.dataset.offerSectionId || 'product-information';
      const url = new URL(sourceUrl, window.location.origin);
      url.searchParams.set('sections', sectionId);

      /** @type {CartOffer[]} */
      let pdpOffers = [];

      try {
        const response = await fetch(url.toString(), {
          headers: { Accept: 'application/json' },
        });

        if (response.ok) {
          const payload = await response.json();
          const html = payload[sectionId];
          if (typeof html === 'string') {
            const doc = new DOMParser().parseFromString(html, 'text/html');
            pdpOffers = this.#parseOffersFromDoc(doc);
          }
        }
      } catch {
        pdpOffers = [];
      }

      if (pdpOffers.length === 0) {
        const pageResponse = await fetch(sourceUrl);
        if (pageResponse.ok) {
          const pageHtml = await pageResponse.text();
          const pageDoc = new DOMParser().parseFromString(pageHtml, 'text/html');
          pdpOffers = this.#parseOffersFromDoc(pageDoc);
        }
      }

      const cached = this.#offers.length ? this.#offers : this.#readCachedOffers();
      const offers =
        pdpOffers.length > 0 ? this.#mergeOffers(pdpOffers, extraCodes) : this.#mergeOffers(cached, extraCodes);
      if (offers.length === 0) return;

      this.#offers = offers;
      this.#writeCachedOffers(offers);
      this.#renderCouponPills(offers);
    } catch {
      const fallback = this.#mergeOffers(this.#offers.length ? this.#offers : this.#readCachedOffers(), extraCodes);
      if (fallback.length === 0) return;
      this.#offers = fallback;
      this.#writeCachedOffers(fallback);
      this.#renderCouponPills(fallback);
    }
  };

  /**
   * @param {CartOffer[]} offers
   */
  #renderCouponPills(offers) {
    const list = this.#pillsList();
    if (!(list instanceof HTMLElement)) return;

    if (Array.isArray(offers) && offers.length) this.#offers = offers;

    const uniqueOffers = [];
    const seen = new Set();
    for (const offer of offers) {
      const normalized = this.#normalizeOffer(offer);
      if (!normalized) continue;
      const key = normalized.code.toUpperCase();
      if (seen.has(key)) continue;
      seen.add(key);
      uniqueOffers.push(normalized);
    }

    if (uniqueOffers.length === 0) {
      list.replaceChildren();
      list.classList.add('hidden');
      return;
    }

    const applied = new Set(this.#existingDiscounts().map((code) => code.toUpperCase()));
    const fragment = document.createDocumentFragment();

    for (const offer of uniqueOffers) {
      const appliedNow = applied.has(offer.code.toUpperCase());
      const eligibility = this.#eligibilityFor(offer);
      const item = document.createElement('li');
      item.className = 'cart-coupon-pills__item';

      const button = document.createElement('button');
      button.type = 'button';
      button.className = 'cart-coupon-pill';
      if (appliedNow) button.classList.add('cart-coupon-pill--applied');
      button.dataset.couponCode = offer.code;
      button.setAttribute('on:click', '/applyCouponPill');
      button.setAttribute('aria-pressed', appliedNow ? 'true' : 'false');
      button.setAttribute(
        'aria-label',
        appliedNow
          ? `Coupon ${offer.code} applied`
          : eligibility.eligible
            ? `Apply coupon ${offer.code}`
            : eligibility.message
      );
      if (eligibility.message) button.title = eligibility.message;
      else if (offer.description) button.title = offer.description;
      button.toggleAttribute('disabled', appliedNow);

      const asCards = Boolean(this.closest('.cro-cart-offers, #cart-drawer'));
      if (asCards) {
        const headline = this.#escapeHtml(this.#offerHeadline(offer));
        const code = this.#escapeHtml(offer.code);
        const desc = this.#escapeHtml(offer.description || '');
        const applyLabel = appliedNow ? 'Applied' : 'Apply';
        const showCode = this.#offerHeadline(offer).toUpperCase() !== offer.code.toUpperCase();
        button.innerHTML = `<span class="cart-coupon-pill__title">${headline}</span>${
          showCode ? `<span class="cart-coupon-pill__code">${code}</span>` : ''
        }${desc ? `<span class="cart-coupon-pill__desc">${desc}</span>` : ''}<span class="cart-coupon-pill__apply">${applyLabel}</span>`;
      } else {
        button.textContent = offer.code;
      }

      item.appendChild(button);
      fragment.appendChild(item);
    }

    list.replaceChildren(fragment);
    list.classList.remove('hidden');
    this.#syncAppliedLabels();
  }

  /**
   * Figma applied banner: "20% OFF applied" (bold), code on the second line.
   */
  #syncAppliedLabels() {
    this.querySelectorAll('.cro-cart-applied[data-discount-code]').forEach((el) => {
      if (!(el instanceof HTMLElement)) return;
      const code = el.dataset.discountCode;
      if (!code) return;
      const offer = this.#offerFor(code) || this.#normalizeOffer({ code });
      const titleEl = el.querySelector('.cro-cart-applied__title');
      if (titleEl) titleEl.textContent = `${this.#offerHeadline(offer)} applied`;
    });
  }

  /**
   * Handles updates to the cart note.
   * @param {SubmitEvent} event - The submit event on our form.
   */
  applyDiscount = async (event) => {
    event.preventDefault();
    event.stopPropagation();

    const form = event.target;
    if (!(form instanceof HTMLFormElement)) return;

    const discountCode = form.querySelector('input[name="discount"]');
    if (!(discountCode instanceof HTMLInputElement)) return;

    await this.#applyDiscountCode(discountCode.value, event, discountCode);
  };

  /**
   * Applies a coupon from a carousel pill.
   * @param {Event} event - The click event on a coupon pill.
   */
  applyCouponPill = async (event) => {
    event.preventDefault();
    event.stopPropagation();

    if (!(event.target instanceof Element)) return;

    const pill = event.target.closest('[data-coupon-code]');
    if (!(pill instanceof HTMLElement) || pill.getAttribute('aria-pressed') === 'true') return;

    const code = pill.dataset.couponCode;
    if (!code) return;

    const input = this.querySelector('input[name="discount"]');
    if (input instanceof HTMLInputElement) input.value = code;

    await this.#applyDiscountCode(code, event, input instanceof HTMLInputElement ? input : null);
  };

  /**
   * Applies a discount code to the cart.
   * @param {string} rawCode - The discount code to apply.
   * @param {Event} event - The originating user event.
   * @param {HTMLInputElement | null} discountCodeInput - The discount input, when present.
   */
  #applyDiscountCode = async (rawCode, event, discountCodeInput) => {
    const { cartDiscountError, cartDiscountErrorDiscountCode, cartDiscountErrorShipping } = this.refs;
    const discountCodeValue = rawCode.trim();

    if (!discountCodeValue || typeof this.dataset.sectionId !== 'string') return;

    const existingDiscounts = this.#existingDiscounts();
    if (existingDiscounts.some((code) => code.toUpperCase() === discountCodeValue.toUpperCase())) return;

    const offer = this.#offerFor(discountCodeValue) || this.#normalizeOffer({ code: discountCodeValue });
    const eligibility = this.#eligibilityFor(offer);
    if (!eligibility.eligible) {
      this.#handleDiscountError('discount_code', eligibility.message);
      return;
    }

    const abortController = this.#createAbortController();

    cartDiscountError.classList.add('hidden');
    cartDiscountErrorDiscountCode.classList.add('hidden');
    cartDiscountErrorShipping.classList.add('hidden');
    this.classList.add('is-loading');

    const allDiscountCodes = [...existingDiscounts, discountCodeValue];
    const deferredPromise = CartDiscountUpdateEvent.createPromise();

    this.dispatchEvent(
      new CartDiscountUpdateEvent({
        discountCodes: allDiscountCodes.map((code) => ({ code })),
        promise: deferredPromise.promise,
      })
    );

    try {
      const config = fetchConfig('json', {
        body: JSON.stringify({
          discount: allDiscountCodes.join(','),
          sections: [this.dataset.sectionId],
        }),
      });

      const response = await fetch(this.#cartUpdateUrl(), {
        ...config,
        signal: abortController.signal,
      });

      const data = await response.json();
      const discountCodes = Array.isArray(data.discount_codes) ? data.discount_codes : [];
      const attempted = discountCodes.find((/** @type {{ code: string; applicable: boolean; }} */ discount) => {
        return discount.code?.toUpperCase() === discountCodeValue.toUpperCase();
      });

      if (attempted && attempted.applicable === false) {
        this.#handleDiscountError(
          'discount_code',
          this.#eligibilityFor(offer).message || offer?.description || ''
        );
        deferredPromise.resolve({
          cart: CartDiscountUpdateEvent.createCartFromAjaxResponse(data),
        });
        return;
      }

      const newHtml = data.sections[this.dataset.sectionId];
      const parsedHtml = new DOMParser().parseFromString(newHtml, 'text/html');
      const section = parsedHtml.getElementById(`shopify-section-${this.dataset.sectionId}`);
      const renderedPills = section?.querySelectorAll('.cart-discount__codes .cart-discount__pill') || [];
      if (section) {
        const codes = Array.from(renderedPills)
          .map((element) => (element instanceof HTMLLIElement ? element.dataset.discountCode : null))
          .filter(Boolean);
        // Before morphing, we need to check if the shipping discount is applicable in the UI
        // we check the liquid logic compared to the cart payload to assess whether we leveraged
        // a valid shipping discount code.
        if (
          codes.length === existingDiscounts.length &&
          codes.every((/** @type {string} */ code) => existingDiscounts.includes(code)) &&
          attempted?.applicable === true
        ) {
          this.#handleDiscountError('shipping');
          if (discountCodeInput) discountCodeInput.value = '';
          deferredPromise.resolve({
            cart: CartDiscountUpdateEvent.createCartFromAjaxResponse(data),
          });
          return;
        }
      }

      deferredPromise.resolve({
        cart: CartDiscountUpdateEvent.createCartFromAjaxResponse(data),
      });
      // Clear the input explicitly: data-skip-node-update on <input name="discount"> means
      // morphSection no longer syncs the input value from the server-rendered empty state,
      // so without this the user's typed code stays in the field after a successful apply.
      if (discountCodeInput) discountCodeInput.value = '';
      await morphSection(this.dataset.sectionId, newHtml, {
        mode: this.closest('theme-drawer') ? 'hydration' : 'full',
      });
      this.#refreshCouponPills();
    } catch (error) {
      deferredPromise.reject(error);
      if (error instanceof Error && error.name !== 'AbortError') {
        this.dispatchEvent(
          new CartErrorEvent({
            error: error.message || 'Failed to apply discount',
            code: 'SERVICE_UNAVAILABLE',
          })
        );
      }
    } finally {
      this.#activeFetch = null;
      this.classList.remove('is-loading');
      cartPerformance.measureFromEvent('discount-update:user-action', event);
    }
  };

  /**
   * Handles removing a discount from the cart.
   * @param {MouseEvent | KeyboardEvent} event - The mouse or keyboard event in our pill.
   */
  removeDiscount = async (event) => {
    event.preventDefault();
    event.stopPropagation();

    if (
      (event instanceof KeyboardEvent && event.key !== 'Enter') ||
      !(event instanceof MouseEvent) ||
      !(event.target instanceof HTMLElement) ||
      typeof this.dataset.sectionId !== 'string'
    ) {
      return;
    }

    const pill = event.target.closest('.cart-discount__pill');
    if (!(pill instanceof HTMLLIElement)) return;

    const discountCode = pill.dataset.discountCode;
    if (!discountCode) return;

    const existingDiscounts = this.#existingDiscounts();
    const index = existingDiscounts.findIndex((code) => code.toUpperCase() === discountCode.toUpperCase());
    if (index === -1) return;

    existingDiscounts.splice(index, 1);

    const abortController = this.#createAbortController();
    const deferredPromise = CartDiscountUpdateEvent.createPromise();

    this.dispatchEvent(
      new CartDiscountUpdateEvent({
        discountCodes: existingDiscounts.map((code) => ({ code })),
        promise: deferredPromise.promise,
      })
    );

    try {
      const config = fetchConfig('json', {
        body: JSON.stringify({ discount: existingDiscounts.join(','), sections: [this.dataset.sectionId] }),
      });

      const response = await fetch(this.#cartUpdateUrl(), {
        ...config,
        signal: abortController.signal,
      });

      const data = await response.json();

      deferredPromise.resolve({
        cart: CartDiscountUpdateEvent.createCartFromAjaxResponse(data),
      });

      await morphSection(this.dataset.sectionId, data.sections[this.dataset.sectionId], {
        mode: this.closest('theme-drawer') ? 'hydration' : 'full',
      });
      this.#refreshCouponPills();
    } catch (error) {
      deferredPromise.reject(error);
      if (error instanceof Error && error.name !== 'AbortError') {
        this.dispatchEvent(
          new CartErrorEvent({
            error: error.message || 'Failed to remove discount',
            code: 'SERVICE_UNAVAILABLE',
          })
        );
      }
    } finally {
      this.#activeFetch = null;
    }
  };

  /**
   * Handles the discount error.
   *
   * @param {'discount_code' | 'shipping'} type - The type of discount error.
   * @param {string} [message] - Optional customer-facing reason.
   */
  #handleDiscountError(type, message) {
    const { cartDiscountError, cartDiscountErrorDiscountCode, cartDiscountErrorShipping } = this.refs;
    if (!this.#defaultDiscountError) {
      this.#defaultDiscountError = cartDiscountErrorDiscountCode.textContent?.trim() || '';
    }
    const target = type === 'discount_code' ? cartDiscountErrorDiscountCode : cartDiscountErrorShipping;
    if (type === 'discount_code') {
      target.textContent = message || this.#defaultDiscountError;
    }
    cartDiscountError.classList.remove('hidden');
    cartDiscountErrorDiscountCode.classList.toggle('hidden', type !== 'discount_code');
    cartDiscountErrorShipping.classList.toggle('hidden', type !== 'shipping');
    target.classList.remove('hidden');

    const errorMessage =
      message || (type === 'discount_code' ? 'Invalid discount code' : 'Discount not applicable for shipping');
    this.dispatchEvent(
      new CartErrorEvent({
        error: errorMessage,
        code: 'VALIDATION_CUSTOM',
      })
    );
  }

  /**
   * Returns an array of existing discount codes.
   * @returns {string[]}
   */
  #existingDiscounts() {
    /** @type {string[]} */
    const discountCodes = [];
    const discountPills = this.querySelectorAll('.cart-discount__codes .cart-discount__pill');
    for (const pill of discountPills) {
      if (pill instanceof HTMLLIElement && typeof pill.dataset.discountCode === 'string') {
        discountCodes.push(pill.dataset.discountCode);
      }
    }

    return discountCodes;
  }
}

if (!customElements.get('cart-discount-component')) {
  customElements.define('cart-discount-component', CartDiscount);
}
